import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmtDatum = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}-${m}-${y}`;
};

const verlofTypeLabel = (key, verlofTypes) => {
  const t = verlofTypes.find(vt => vt.key === key);
  return t ? t.text : key;
};

const statusLabel = (s) => {
  const map = { ingediend: 'Ingediend', goedgekeurd: 'Goedgekeurd', afgekeurd: 'Afgekeurd', ingetrokken: 'Ingetrokken' };
  return map[s] || s;
};

/**
 * Genereer PDF overzicht voor één medewerker
 */
export function genereerMedewerkerPDF(medewerker, verlofaanvragen, overuren, verlofTypes, jaar, extra = {}) {
  const { roosterWijzigingen = [], flexPlanning = [], flexUren = [], flexUitbetalingen = [] } = extra;
  const doc = new jsPDF();
  const pw = doc.internal.pageSize.getWidth();
  let y = 20;

  // === HEADER ===
  doc.setFillColor(26, 26, 46);
  doc.rect(0, 0, pw, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Slaaploods Verlofoverzicht', 14, 18);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`${medewerker.Naam} — ${jaar}`, 14, 30);
  doc.setFontSize(9);
  doc.text(`Gegenereerd: ${new Date().toLocaleDateString('nl-NL')}`, pw - 14, 30, { align: 'right' });

  y = 50;
  doc.setTextColor(0, 0, 0);

  // === MEDEWERKER INFO ===
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Gegevens medewerker', 14, y);
  y += 8;

  const infoData = [
    ['Naam', medewerker.Naam],
    ['E-mail', medewerker.Email],
    ['Afdeling', medewerker.Afdeling || '—'],
    ['Contracturen/week', `${medewerker.ContractUren || 0}u`],
    ['In dienst sinds', fmtDatum(medewerker.IndienstDatum)],
    ['Status', (medewerker.Status || 'actief').charAt(0).toUpperCase() + (medewerker.Status || 'actief').slice(1)],
  ];

  autoTable(doc, {
    startY: y,
    head: [],
    body: infoData,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } },
    margin: { left: 14, right: 14 },
  });

  y = doc.lastAutoTable.finalY + 12;

  // === SALDO OVERZICHT ===
  const mwAanvragen = verlofaanvragen.filter(v =>
    v.medewerkerId === medewerker.Id &&
    new Date(v.StartDatum).getFullYear() === jaar
  );
  const goedgekeurd = mwAanvragen.filter(v => v.Status === 'goedgekeurd');

  const perType = {};
  goedgekeurd.forEach(v => {
    const key = v.Type || 'onbekend';
    if (!perType[key]) perType[key] = { uren: 0, aantal: 0 };
    perType[key].uren += (v.Uren || 0);
    perType[key].aantal += 1;
  });

  const verlofOpgenomen = goedgekeurd
    .filter(v => {
      const vt = verlofTypes.find(t => t.key === v.Type);
      return vt && vt.aftrekSaldo !== 'flex' && vt.aftrekSaldo !== 'geen';
    })
    .reduce((sum, v) => sum + Math.max(0, (v.Uren || 0) - (v.overurenGebruikt || 0)), 0);

  const totaalVerlof = (medewerker.BerekendeVerlofUren || 0) + (medewerker.MeegenomenUren || 0);
  const resterend = totaalVerlof - verlofOpgenomen;

  const mwOveruren = overuren.filter(o =>
    o.medewerkerId === medewerker.Id &&
    o.Status === 'goedgekeurd' &&
    new Date(o.Datum || o.datum).getFullYear() === jaar
  );
  const overurenTotaal = mwOveruren.reduce((s, o) => s + (o.Uren || o.uren || 0), 0);
  const overurenGebruikt = goedgekeurd.reduce((s, v) => s + (v.overurenGebruikt || 0), 0);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Saldo overzicht', 14, y);
  y += 8;

  const saldoData = [
    ['Verlofuren (contract)', `${medewerker.BerekendeVerlofUren || 0}u`],
    ['Meegenomen vorig jaar', `${medewerker.MeegenomenUren || 0}u`],
    ['Totaal beschikbaar', `${totaalVerlof}u`],
    ['Opgenomen verlof', `${verlofOpgenomen}u`],
    ['Resterend', `${resterend}u`],
    ['', ''],
    ['Overuren verdiend', `${overurenTotaal}u`],
    ['Overuren ingezet', `${overurenGebruikt}u`],
    ['Overuren saldo', `${overurenTotaal - overurenGebruikt}u`],
  ];

  autoTable(doc, {
    startY: y,
    head: [],
    body: saldoData,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { halign: 'right', cellWidth: 30 },
    },
    margin: { left: 14, right: 14 },
    didParseCell: (data) => {
      if (data.row.index === 2 || data.row.index === 4 || data.row.index === 8) {
        data.cell.styles.fontStyle = 'bold';
      }
      if (data.row.index === 4 && data.column.index === 1) {
        data.cell.styles.textColor = resterend >= 0 ? [16, 124, 16] : [209, 52, 56];
      }
    },
  });

  y = doc.lastAutoTable.finalY + 12;

  // === VERDELING PER TYPE ===
  if (Object.keys(perType).length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Verdeling per type', 14, y);
    y += 8;

    const typeRows = Object.entries(perType).map(([key, val]) => [
      verlofTypeLabel(key, verlofTypes),
      String(val.aantal),
      `${val.uren}u`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Type', 'Aantal', 'Uren']],
      body: typeRows,
      theme: 'grid',
      headStyles: { fillColor: [232, 110, 28], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      styles: { fontSize: 10, cellPadding: 4 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });

    y = doc.lastAutoTable.finalY + 12;
  }

  // === VERLOFAANVRAGEN LIJST ===
  if (y > 230) { doc.addPage(); y = 20; }

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Verlofaanvragen', 14, y);
  y += 8;

  if (mwAanvragen.length === 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text('Geen verlofaanvragen in dit jaar.', 14, y);
    y += 12;
  } else {
    const aanvraagRows = mwAanvragen
      .sort((a, b) => a.StartDatum.localeCompare(b.StartDatum))
      .map(v => [
        verlofTypeLabel(v.Type, verlofTypes),
        fmtDatum(v.StartDatum),
        fmtDatum(v.EindDatum),
        v.StartTijd ? `${v.StartTijd}–${v.EindTijd || ''}` : 'Hele dag',
        `${v.Uren}u`,
        statusLabel(v.Status),
        v.IngediendDoorNaam || v.ingediendDoorNaam || '',
        v.Reden || '',
      ]);

    autoTable(doc, {
      startY: y,
      head: [['Type', 'Van', 'Tot', 'Tijden', 'Uren', 'Status', 'Door', 'Opmerking']],
      body: aanvraagRows,
      theme: 'striped',
      headStyles: { fillColor: [26, 26, 46], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 3, overflow: 'ellipsize' },
      columnStyles: {
        0: { cellWidth: 28 }, 1: { cellWidth: 22 }, 2: { cellWidth: 22 },
        3: { cellWidth: 24 }, 4: { cellWidth: 14, halign: 'right' },
        5: { cellWidth: 22 }, 6: { cellWidth: 'auto' },
      },
      margin: { left: 14, right: 14 },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 5) {
          const status = data.cell.raw;
          if (status === 'Goedgekeurd') data.cell.styles.textColor = [16, 124, 16];
          else if (status === 'Afgekeurd') data.cell.styles.textColor = [209, 52, 56];
          else if (status === 'Ingediend') data.cell.styles.textColor = [180, 140, 0];
        }
      },
    });

    y = doc.lastAutoTable.finalY + 12;
  }

  // === OVERUREN LIJST ===
  if (mwOveruren.length > 0) {
    if (y > 240) { doc.addPage(); y = 20; }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Overuren', 14, y);
    y += 8;

    const ouRows = mwOveruren
      .sort((a, b) => (a.datum || a.Datum || '').localeCompare(b.datum || b.Datum || ''))
      .map(o => [
        fmtDatum(o.datum || o.Datum),
        `${o.uren || o.Uren}u`,
        o.reden || o.Reden || '',
      ]);

    autoTable(doc, {
      startY: y,
      head: [['Datum', 'Uren', 'Reden']],
      body: ouRows,
      theme: 'striped',
      headStyles: { fillColor: [26, 26, 46], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // === CONTRACTWIJZIGINGEN ===
  const cw = medewerker.ContractWijzigingen || [];
  if (cw.length > 0) {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Contractwijzigingen', 14, y);
    y += 8;

    const cwRows = cw
      .sort((a, b) => (a.ingangsDatum || '').localeCompare(b.ingangsDatum || ''))
      .map(c => [fmtDatum(c.ingangsDatum), `${c.contractUren}u/week`, c.reden || '—']);

    autoTable(doc, {
      startY: y,
      head: [['Ingangsdatum', 'Nieuwe uren', 'Reden']],
      body: cwRows,
      theme: 'striped',
      headStyles: { fillColor: [26, 26, 46], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // === ROOSTERWIJZIGINGEN ===
  const mwRw = roosterWijzigingen.filter(r =>
    r.medewerkerId === medewerker.Id && new Date(r.datum).getFullYear() === jaar
  );
  if (mwRw.length > 0) {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Roosterwijzigingen', 14, y);
    y += 8;

    const rwRows = mwRw
      .sort((a, b) => (a.datum || '').localeCompare(b.datum || ''))
      .map(r => [
        fmtDatum(r.datum),
        r.heleDag ? 'Hele dag' : `${r.startTijd || ''} – ${r.eindTijd || ''}`,
        r.vervangDatum ? `Vervangt ${fmtDatum(r.vervangDatum)}` : '',
        r.omschrijving || '—',
      ]);

    autoTable(doc, {
      startY: y,
      head: [['Datum', 'Tijden', 'Vervanging', 'Omschrijving']],
      body: rwRows,
      theme: 'striped',
      headStyles: { fillColor: [26, 26, 46], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 3 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // === FLEX PLANNING & UREN (als flexkracht) ===
  const mwFlexPlan = flexPlanning.filter(p => p.flexkrachtId === medewerker.Id && new Date(p.datum).getFullYear() === jaar);
  const mwFlexUren = flexUren.filter(u => u.flexkrachtId === medewerker.Id && new Date(u.datum).getFullYear() === jaar);
  const mwFlexUitb = flexUitbetalingen.filter(u => u.flexkrachtId === medewerker.Id && new Date(u.datum).getFullYear() === jaar);

  if (mwFlexPlan.length > 0 || mwFlexUren.length > 0) {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Flex planning & uren', 14, y);
    y += 8;

    if (mwFlexPlan.length > 0) {
      const fpRows = mwFlexPlan
        .sort((a, b) => (a.datum || '').localeCompare(b.datum || ''))
        .map(p => [fmtDatum(p.datum), `${p.startTijd || ''} – ${p.eindTijd || ''}`, `${p.uren || 0}u`, p.omschrijving || '—']);

      autoTable(doc, {
        startY: y,
        head: [['Datum', 'Tijden', 'Uren', 'Omschrijving']],
        body: fpRows,
        theme: 'striped',
        headStyles: { fillColor: [0, 120, 212], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        styles: { fontSize: 9, cellPadding: 3 },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (mwFlexUren.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Gewerkte uren', 14, y);
      y += 6;

      const fuRows = mwFlexUren
        .sort((a, b) => (a.datum || '').localeCompare(b.datum || ''))
        .map(u => [fmtDatum(u.datum), `${u.startTijd || ''} – ${u.eindTijd || ''}`, `${u.uren || 0}u`, u.omschrijving || '—']);

      autoTable(doc, {
        startY: y,
        head: [['Datum', 'Tijden', 'Uren', 'Omschrijving']],
        body: fuRows,
        theme: 'striped',
        headStyles: { fillColor: [16, 124, 16], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        styles: { fontSize: 9, cellPadding: 3 },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (mwFlexUitb.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Uitbetalingen', 14, y);
      y += 6;

      const ubRows = mwFlexUitb
        .sort((a, b) => (a.datum || '').localeCompare(b.datum || ''))
        .map(u => [fmtDatum(u.datum), `${u.uren || 0}u`, u.type || '—', u.omschrijving || '—']);

      autoTable(doc, {
        startY: y,
        head: [['Datum', 'Uren', 'Type', 'Omschrijving']],
        body: ubRows,
        theme: 'striped',
        headStyles: { fillColor: [232, 110, 28], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        styles: { fontSize: 9, cellPadding: 3 },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 12;
    }
  }

  // === ALLE MUTATIES CHRONOLOGISCH ===
  {
    doc.addPage();
    y = 20;
    doc.setFillColor(26, 26, 46);
    doc.rect(0, 0, pw, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Chronologisch mutatieoverzicht — ${medewerker.Naam}`, 14, 20);
    y = 40;
    doc.setTextColor(0, 0, 0);

    // Verzamel alle mutaties in 1 lijst
    const alleMutaties = [];

    // Verlofaanvragen
    mwAanvragen.forEach(v => {
      alleMutaties.push({
        datum: v.StartDatum,
        categorie: 'Verlof',
        type: verlofTypeLabel(v.Type, verlofTypes),
        detail: `${fmtDatum(v.StartDatum)} t/m ${fmtDatum(v.EindDatum)}` + (v.StartTijd ? ` (${v.StartTijd}–${v.EindTijd || ''})` : ''),
        uren: `${v.Uren || 0}u`,
        status: statusLabel(v.Status),
        door: v.IngediendDoorNaam || v.ingediendDoorNaam || '',
      });
    });

    // Overuren
    const mwOveruren = overuren.filter(o => o.medewerkerId === medewerker.Id && new Date(o.datum || o.Datum).getFullYear() === jaar);
    mwOveruren.forEach(o => {
      alleMutaties.push({
        datum: o.datum || o.Datum,
        categorie: 'Overuren',
        type: o.soort === 'zaterdag' ? 'Zaterdag' : 'Werkdag',
        detail: fmtDatum(o.datum || o.Datum) + (o.startTijd ? ` (${o.startTijd}–${o.eindTijd || ''})` : ''),
        uren: `${o.uren || o.Uren || 0}u`,
        status: statusLabel(o.Status || o.status),
        door: o.ingediendDoorNaam || '',
      });
    });

    // Contractwijzigingen
    (medewerker.ContractWijzigingen || []).forEach(c => {
      alleMutaties.push({
        datum: c.ingangsDatum,
        categorie: 'Contract',
        type: 'Contractwijziging',
        detail: `Nieuwe uren: ${c.contractUren}u/week`,
        uren: '',
        status: 'Doorgevoerd',
        door: c.reden || '',
      });
    });

    // Roosterwijzigingen
    const mwRw2 = roosterWijzigingen.filter(r => r.medewerkerId === medewerker.Id && new Date(r.datum).getFullYear() === jaar);
    mwRw2.forEach(r => {
      alleMutaties.push({
        datum: r.datum,
        categorie: 'Rooster',
        type: 'Roosterwijziging',
        detail: r.heleDag ? 'Hele dag' : `${r.startTijd || ''} – ${r.eindTijd || ''}`,
        uren: '',
        status: r.vervangDatum ? `Vervangt ${fmtDatum(r.vervangDatum)}` : '',
        door: r.omschrijving || '',
      });
    });

    // Flex planning
    const mwFP2 = flexPlanning.filter(p => p.flexkrachtId === medewerker.Id && new Date(p.datum).getFullYear() === jaar);
    mwFP2.forEach(p => {
      alleMutaties.push({
        datum: p.datum,
        categorie: 'Flex',
        type: 'Ingepland',
        detail: `${fmtDatum(p.datum)} (${p.startTijd || ''} – ${p.eindTijd || ''})`,
        uren: `${p.uren || 0}u`,
        status: '',
        door: p.omschrijving || '',
      });
    });

    // Flex uren
    const mwFU2 = flexUren.filter(u => u.flexkrachtId === medewerker.Id && new Date(u.datum).getFullYear() === jaar);
    mwFU2.forEach(u => {
      alleMutaties.push({
        datum: u.datum,
        categorie: 'Flex',
        type: 'Gewerkt',
        detail: `${fmtDatum(u.datum)} (${u.startTijd || ''} – ${u.eindTijd || ''})`,
        uren: `${u.uren || 0}u`,
        status: '',
        door: u.omschrijving || '',
      });
    });

    // Uitbetalingen / correcties
    const mwUB2 = flexUitbetalingen.filter(u =>
      (u.flexkrachtId === medewerker.Id || u.medewerkerId === medewerker.Id) &&
      new Date(u.datum).getFullYear() === jaar
    );
    mwUB2.forEach(u => {
      alleMutaties.push({
        datum: u.datum,
        categorie: 'Saldo',
        type: u.type === 'correctie' ? 'Correctie' : u.type === 'overuren-uitbetaling' ? 'OU uitbetaling' : 'Uitbetaling',
        detail: u.omschrijving || '',
        uren: `${u.uren || 0}u`,
        status: '',
        door: '',
      });
    });

    // Sorteer chronologisch
    alleMutaties.sort((a, b) => (a.datum || '').localeCompare(b.datum || ''));

    if (alleMutaties.length === 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.text('Geen mutaties in dit jaar.', 14, y);
      y += 12;
    } else {
      const mutRows = alleMutaties.map(m => [
        fmtDatum(m.datum),
        m.categorie,
        m.type,
        m.detail,
        m.uren,
        m.status,
        m.door,
      ]);

      autoTable(doc, {
        startY: y,
        head: [['Datum', 'Cat.', 'Type', 'Detail', 'Uren', 'Status', 'Door/Opmerking']],
        body: mutRows,
        theme: 'striped',
        headStyles: { fillColor: [26, 26, 46], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        styles: { fontSize: 7, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 16 },
          2: { cellWidth: 26 },
          4: { cellWidth: 14, halign: 'right' },
          5: { cellWidth: 22 },
        },
        margin: { left: 10, right: 10 },
        didDrawPage: () => {
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.text(`Slaaploods Verlofbeheer — Alle mutaties ${medewerker.Naam} ${jaar}`, pw / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
        },
      });
    }
  }

  // === FOOTER ===
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Slaaploods Verlofbeheer — Pagina ${i} van ${pageCount}`, pw / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
  }

  return doc;
}

/**
 * Genereer totaaloverzicht PDF voor medewerkers over een periode
 * @param {Array} medewerkers - gefilterde medewerkers
 * @param {Array} verlofaanvragen - alle verlofaanvragen
 * @param {Array} overuren - alle overuren
 * @param {Array} verlofTypes - verloftypes
 * @param {Array} feestdagen - feestdagen lijst
 * @param {string} vanDatum - start periode (YYYY-MM-DD)
 * @param {string} totDatum - eind periode (YYYY-MM-DD)
 */
export function genereerTotaaloverzichtPDF(medewerkers, verlofaanvragen, overuren, verlofTypes, feestdagen, vanDatum, totDatum, options = {}) {
  const { inclMutaties = false } = options;
  const doc = new jsPDF('landscape');
  const pw = doc.internal.pageSize.getWidth();
  let y = 20;

  const vandaag = new Date().toISOString().split('T')[0];
  const feestdagSet = new Set((feestdagen || []).map(f => f.datum || f.Datum));

  // Helper: bereken werkuren tussen twee datums op basis van werkrooster
  const berekenWerkuren = (start, eind, werkrooster) => {
    const dagKeys = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
    let uren = 0;
    const d = new Date(start + 'T12:00:00');
    const eindD = new Date(eind + 'T12:00:00');
    while (d <= eindD) {
      const dStr = d.toISOString().split('T')[0];
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6 && !feestdagSet.has(dStr)) {
        if (werkrooster) {
          const dagKey = dagKeys[dow];
          const rooster = werkrooster[dagKey];
          if (rooster) {
            let dagUren = 0;
            for (const blok of ['ochtend', 'middag', 'avond']) {
              const tijdStr = rooster[blok];
              if (!tijdStr || !tijdStr.includes('-')) continue;
              const [s, e] = tijdStr.split('-').map(t => t.trim());
              if (s && e) {
                const [sh, sm] = s.split(':').map(Number);
                const [eh, em] = e.split(':').map(Number);
                dagUren += (eh + em / 60) - (sh + sm / 60);
              }
            }
            if (dagUren > 0) uren += dagUren;
          }
        } else {
          uren += 8;
        }
      }
      d.setDate(d.getDate() + 1);
    }
    return Math.round(uren * 10) / 10;
  };

  // Header
  doc.setFillColor(26, 26, 46);
  doc.rect(0, 0, pw, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  const titel = medewerkers.length === 1
    ? `Slaaploods — Verlofoverzicht ${medewerkers[0].Naam}`
    : 'Slaaploods — Totaaloverzicht Verlofuren';
  doc.text(titel, 14, 16);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Periode: ${fmtDatum(vanDatum)} t/m ${fmtDatum(totDatum)} — Gegenereerd: ${new Date().toLocaleDateString('nl-NL')}`, 14, 28);

  y = 44;
  doc.setTextColor(0, 0, 0);

  const actieveMedewerkers = [...medewerkers].sort((a, b) => a.Naam.localeCompare(b.Naam));

  const rows = actieveMedewerkers.map(m => {
    const mwAanvragen = verlofaanvragen.filter(v => {
      if (v.medewerkerId !== m.Id) return false;
      const vStatus = v.Status || v.status;
      if (vStatus !== 'goedgekeurd' && vStatus !== 'ingediend') return false;
      const start = v.StartDatum || v.startDatum;
      if (!start) return false;
      if (v.isOpenZiek) return start <= totDatum;
      const eind = v.EindDatum || v.eindDatum || start;
      return start <= totDatum && eind >= vanDatum;
    });

    const goedgekeurd = mwAanvragen.filter(v => (v.Status || v.status) === 'goedgekeurd');

    // Ziek uren (inclusief open ziekmeldingen)
    let ziekUren = 0;
    goedgekeurd.filter(v => (v.Type || v.type) === 'ziek').forEach(v => {
      if (v.isOpenZiek) {
        const start = v.StartDatum || v.startDatum;
        const eind = vandaag < totDatum ? vandaag : totDatum;
        const effectieveStart = start > vanDatum ? start : vanDatum;
        if (effectieveStart <= eind) {
          ziekUren += berekenWerkuren(effectieveStart, eind, m.Werkrooster);
        }
      } else {
        ziekUren += (v.Uren || v.uren || 0);
      }
    });

    // Flex/ADV uren
    const flexAdvUren = goedgekeurd
      .filter(v => { const t = v.Type || v.type; return t === 'flexADV' || t === 'adv' || t === 'flex-min'; })
      .reduce((s, v) => s + (v.Uren || v.uren || 0), 0);

    // Regulier verlof opgenomen (aftrek saldo)
    const verlofOpgenomen = goedgekeurd
      .filter(v => {
        const vt = verlofTypes.find(t => t.key === (v.Type || v.type));
        return vt && vt.aftrekSaldo !== 'flex' && vt.aftrekSaldo !== 'geen';
      })
      .reduce((sum, v) => sum + Math.max(0, (v.Uren || v.uren || 0) - (v.overurenGebruikt || 0)), 0);

    // Split verleden (opgenomen) vs toekomst (gepland)
    const verlofVerleden = goedgekeurd
      .filter(v => {
        const vt = verlofTypes.find(t => t.key === (v.Type || v.type));
        if (!vt || vt.aftrekSaldo === 'flex' || vt.aftrekSaldo === 'geen') return false;
        return (v.StartDatum || v.startDatum) <= vandaag;
      })
      .reduce((sum, v) => sum + Math.max(0, (v.Uren || v.uren || 0) - (v.overurenGebruikt || 0)), 0);
    const verlofGepland = verlofOpgenomen - verlofVerleden;

    // Ingediend (wacht op goedkeuring)
    const ingediendUren = mwAanvragen
      .filter(v => (v.Status || v.status) === 'ingediend')
      .reduce((s, v) => s + (v.Uren || v.uren || 0), 0);

    // Overuren
    const mwOveruren = overuren.filter(o => {
      if (o.medewerkerId !== m.Id || o.Status !== 'goedgekeurd') return false;
      const d = o.Datum || o.datum;
      return d >= vanDatum && d <= totDatum;
    });
    const ouVerdiend = mwOveruren.reduce((s, o) => s + (o.Uren || o.uren || 0), 0);
    const ouGebruikt = goedgekeurd.reduce((s, v) => s + (v.overurenGebruikt || 0), 0);

    const berekendeUren = m.BerekendeVerlofUren || 0;
    const meegenomen = m.MeegenomenUren || 0;
    const totaal = berekendeUren + meegenomen;
    const resterend = totaal - verlofOpgenomen;

    return [
      m.Naam,
      `${m.ContractUren || 0}u`,
      `${totaal}u`,
      `${verlofVerleden}u`,
      verlofGepland > 0 ? `${verlofGepland}u` : '',
      ingediendUren > 0 ? `${ingediendUren}u` : '',
      `${resterend}u`,
      ziekUren > 0 ? `${Math.round(ziekUren * 10) / 10}u` : '',
      flexAdvUren > 0 ? `${flexAdvUren}u` : '',
      ouVerdiend > 0 ? `${ouVerdiend}u` : '',
      ouGebruikt > 0 ? `${ouGebruikt}u` : '',
      `${ouVerdiend - ouGebruikt}u`,
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['Naam', 'Contract', 'Verlof\ntotaal', 'Opge-\nnomen', 'Gepland', 'Inge-\ndiend', 'Reste-\nrend', 'Ziek', 'Flex/\nADV', 'OU\nverd.', 'OU\ngebr.', 'OU\nsaldo']],
    body: rows,
    theme: 'grid',
    headStyles: { fillColor: [232, 110, 28], textColor: 255, fontStyle: 'bold', fontSize: 7, cellPadding: 3, valign: 'middle' },
    styles: { fontSize: 8, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 35, fontStyle: 'bold' },
      6: { fontStyle: 'bold' },
    },
    margin: { left: 10, right: 10 },
    didParseCell: (data) => {
      if (data.section === 'body') {
        // Resterend: groen/rood
        if (data.column.index === 6) {
          const val = parseFloat(data.cell.raw);
          data.cell.styles.textColor = val >= 0 ? [16, 124, 16] : [209, 52, 56];
        }
        // Ziek: oranje
        if (data.column.index === 7 && data.cell.raw && data.cell.raw !== '') {
          data.cell.styles.textColor = [232, 110, 28];
        }
        // Gepland: blauw
        if (data.column.index === 4 && data.cell.raw && data.cell.raw !== '') {
          data.cell.styles.textColor = [0, 120, 212];
        }
        // Ingediend: goudgeel
        if (data.column.index === 5 && data.cell.raw && data.cell.raw !== '') {
          data.cell.styles.textColor = [180, 140, 0];
        }
      }
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // Legenda
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Opgenomen = goedgekeurd verlof in verleden (voor ${new Date().toLocaleDateString('nl-NL')})  |  Gepland = goedgekeurd in de toekomst  |  Ingediend = wacht op goedkeuring`, 10, y);
  y += 4;
  doc.text(`Ziek bij open ziekmeldingen = berekend op werkdagen t/m vandaag  |  Flex/ADV = Flex- en ADV-uren (geen aftrek verlof)  |  OU = overuren`, 10, y);

  // ═══ MUTATIELOGBOEK ═══
  if (inclMutaties) {
    const actieveMwSorted = [...actieveMedewerkers].sort((a, b) => a.Naam.localeCompare(b.Naam));

    actieveMwSorted.forEach(m => {
      // Verlofaanvragen voor deze medewerker in de periode
      const mwVerlof = verlofaanvragen.filter(v => {
        if (v.medewerkerId !== m.Id) return false;
        const start = v.StartDatum || v.startDatum;
        if (!start) return false;
        if (v.isOpenZiek) return start <= totDatum;
        const eind = v.EindDatum || v.eindDatum || start;
        return start <= totDatum && eind >= vanDatum;
      }).sort((a, b) => ((a.StartDatum || a.startDatum) || '').localeCompare((b.StartDatum || b.startDatum) || ''));

      const mwOveruren = overuren.filter(o => {
        if (o.medewerkerId !== m.Id) return false;
        const d = o.Datum || o.datum;
        return d >= vanDatum && d <= totDatum;
      }).sort((a, b) => ((a.Datum || a.datum) || '').localeCompare((b.Datum || b.datum) || ''));

      if (mwVerlof.length === 0 && mwOveruren.length === 0) return;

      // Nieuwe pagina per medewerker
      doc.addPage('landscape');
      let my = 20;

      // Medewerker header
      doc.setFillColor(30, 58, 95);
      doc.rect(0, 0, pw, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Mutatielogboek  --  ${m.Naam}`, 14, 16);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`${fmtDatum(vanDatum)} t/m ${fmtDatum(totDatum)}`, pw - 14, 16, { align: 'right' });

      my = 35;
      doc.setTextColor(0, 0, 0);

      // Verlofaanvragen tabel
      if (mwVerlof.length > 0) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Verlof & Ziekmeldingen', 14, my);
        my += 6;

        const verlofRows = mwVerlof.map(v => {
          const type = verlofTypeLabel(v.Type || v.type, verlofTypes);
          const start = fmtDatum(v.StartDatum || v.startDatum);
          const eind = v.isOpenZiek ? '(open)' : fmtDatum(v.EindDatum || v.eindDatum || v.StartDatum || v.startDatum);
          const uren = v.isOpenZiek ? 'lopend' : `${v.Uren || v.uren || 0}u`;
          const status = statusLabel(v.Status || v.status);
          const tijden = (v.BeginTijd || v.beginTijd) ? `${v.BeginTijd || v.beginTijd}–${v.EindTijd || v.eindTijd || ''}` : 'hele dag';
          const opmerking = (v.Reden || v.reden || '—').substring(0, 60);
          return [type, start, eind, tijden, uren, status, opmerking];
        });

        autoTable(doc, {
          startY: my,
          head: [['Type', 'Startdatum', 'Einddatum', 'Tijden', 'Uren', 'Status', 'Opmerking']],
          body: verlofRows,
          theme: 'striped',
          headStyles: { fillColor: [0, 120, 212], textColor: 255, fontStyle: 'bold', fontSize: 7, cellPadding: 2.5 },
          styles: { fontSize: 7, cellPadding: 2.5 },
          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 30 }, 6: { cellWidth: 60 } },
          margin: { left: 10, right: 10 },
          didParseCell: (data) => {
            if (data.section === 'body') {
              if (data.column.index === 5) {
                const s = data.cell.raw;
                if (s === 'Goedgekeurd') data.cell.styles.textColor = [16, 124, 16];
                else if (s === 'Afgekeurd') data.cell.styles.textColor = [209, 52, 56];
                else if (s === 'Ingediend') data.cell.styles.textColor = [180, 140, 0];
              }
            }
          },
        });
        my = doc.lastAutoTable.finalY + 10;
      }

      // Overuren tabel
      if (mwOveruren.length > 0) {
        if (my > doc.internal.pageSize.getHeight() - 40) {
          doc.addPage('landscape');
          my = 20;
        }

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Overuren', 14, my);
        my += 6;

        const ouRows = mwOveruren.map(o => [
          fmtDatum(o.Datum || o.datum),
          `${o.Uren || o.uren || 0}u`,
          statusLabel(o.Status || o.status),
          (o.Omschrijving || o.omschrijving || '—').substring(0, 80),
        ]);

        autoTable(doc, {
          startY: my,
          head: [['Datum', 'Uren', 'Status', 'Omschrijving']],
          body: ouRows,
          theme: 'striped',
          headStyles: { fillColor: [232, 110, 28], textColor: 255, fontStyle: 'bold', fontSize: 7, cellPadding: 2.5 },
          styles: { fontSize: 7, cellPadding: 2.5 },
          columnStyles: { 3: { cellWidth: 100 } },
          margin: { left: 10, right: 10 },
        });
      }
    });
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Slaaploods Verlofbeheer — Pagina ${i} van ${pageCount}`, pw / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
  }

  return doc;
}
